/**
 * Herramientas internas — la comprobación de la representación
 * (`TDS-CORE` Parte 4 §3.1, `OD-0031` D-05, `OD-0020` D-02 enmendada).
 *
 * Vive fuera del panel del cliente, en `/interno`, y no se enlaza desde
 * ninguna parte: quien no es operador recibe un 404 de la API y aquí sólo ve
 * "no se ha encontrado". No se confirma que exista un backoffice.
 *
 * Lo que la persona hace ANTES de pulsar, en la Sede y con el certificado de
 * Jominicol: "Consultar apoderamientos recibidos" y comprobar que el
 * poderdante es ese NIF, que el apoderado somos nosotros, que el poder es
 * general o el específico de VERI*FACTU, y que está vigente. La pantalla lo
 * recuerda en cada ficha, porque es lo único que evita activar por error.
 */
import { useCallback, useEffect, useState } from "react";
import { api, type MotivoRechazoAutorizacion, type PendienteOperador } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Selector, Tarjeta } from "../ui/primitivas";

const MOTIVOS: ReadonlyArray<{ readonly valor: MotivoRechazoAutorizacion; readonly texto: string }> = [
  { valor: "NOT_FOUND", texto: t.operador.motivos.NOT_FOUND },
  { valor: "GRANTOR_MISMATCH", texto: t.operador.motivos.GRANTOR_MISMATCH },
  { valor: "WRONG_PROCEDURE", texto: t.operador.motivos.WRONG_PROCEDURE },
  { valor: "WRONG_REPRESENTATIVE", texto: t.operador.motivos.WRONG_REPRESENTATIVE },
  { valor: "NOT_IN_FORCE", texto: t.operador.motivos.NOT_IN_FORCE },
  { valor: "OTHER", texto: t.operador.motivos.OTHER },
];

function fechaLegible(iso: string | null): string {
  if (iso === null) return "";
  return new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function PantallaOperador() {
  const [pendientes, setPendientes] = useState<readonly PendienteOperador[] | null>(null);
  const [estadoCarga, setEstadoCarga] = useState<"cargando" | "ok" | "no-encontrado" | "sin-mfa" | "error">("cargando");
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<{ tono: "exito" | "error"; texto: string } | null>(null);
  const [rechazando, setRechazando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState<MotivoRechazoAutorizacion>("NOT_FOUND");

  const cargar = useCallback(async (): Promise<void> => {
    const r = await api.pendientesInternos();
    if (r.ok) {
      setPendientes(r.datos);
      setEstadoCarga("ok");
      return;
    }
    if (r.estado === 404 || r.estado === 401) setEstadoCarga("no-encontrado");
    else if (r.estado === 403) setEstadoCarga("sin-mfa");
    else setEstadoCarga("error");
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function decidir(p: PendienteOperador, decision: "ACTIVE" | "REJECTED"): Promise<void> {
    setMensaje(null);
    setOcupado(p.organizationId);
    const r = await api.revisarInterno(p.organizationId, decision, decision === "REJECTED" ? motivo : undefined);
    setOcupado(null);
    if (r.ok) {
      setRechazando(null);
      setMensaje({
        tono: "exito",
        texto: decision === "ACTIVE" ? t.operador.validada(p.legalName) : t.operador.rechazada(p.legalName),
      });
      await cargar();
      return;
    }
    setMensaje({ tono: "error", texto: r.estado === 0 ? t.comun.sinRed : t.operador.errorDecision });
    await cargar();
  }

  if (estadoCarga === "cargando") {
    return <Tarjeta titulo={t.operador.titulo}><p className="ayuda-neutra">{t.operador.cargando}</p></Tarjeta>;
  }
  // Mismo texto para "no eres operador" y "no hay sesión": no se distingue.
  if (estadoCarga === "no-encontrado") return <Tarjeta titulo={t.operador.noEncontradoTitulo}><p>{t.operador.noEncontrado}</p></Tarjeta>;
  if (estadoCarga === "sin-mfa") {
    return (
      <Tarjeta titulo={t.operador.titulo}>
        <Aviso tono="aviso">{t.operador.sinMfa}</Aviso>
      </Tarjeta>
    );
  }
  if (estadoCarga === "error" || pendientes === null) {
    return (
      <Tarjeta titulo={t.operador.titulo}>
        <Aviso tono="error">{t.comun.errorGenerico}</Aviso>
        <Boton variante="secundario" onClick={() => void cargar()}>{t.operador.reintentar}</Boton>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta titulo={t.operador.titulo}>
      <p>{t.operador.detalle}</p>
      <p>
        <a href={t.operador.enlaceSede} target="_blank" rel="noopener noreferrer">
          {t.operador.enlaceSedeTexto}
        </a>
      </p>
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}

      {pendientes.length === 0 ? (
        <p className="ayuda-neutra">{t.operador.vacio}</p>
      ) : (
        <ul className="lista-filas">
          {pendientes.map((p) => (
            <li key={p.organizationId} className="fila fila-operador">
              <span className="fila-texto">
                <span className="fila-titulo">{p.legalName}</span>
                <span className="ayuda-neutra">
                  {t.operador.nif(p.taxId)} · {t.operador.declarado(fechaLegible(p.declaredAt))}
                </span>
              </span>
              <span className="acciones-operador">
                <Boton onClick={() => void decidir(p, "ACTIVE")} deshabilitado={ocupado !== null}>
                  {t.operador.validar}
                </Boton>
                <Boton
                  variante="secundario"
                  onClick={() => setRechazando(rechazando === p.organizationId ? null : p.organizationId)}
                  deshabilitado={ocupado !== null}
                >
                  {t.operador.rechazar}
                </Boton>
              </span>
              {rechazando === p.organizationId ? (
                <span className="rechazo-operador">
                  <Selector
                    id={`motivo-${p.organizationId}`}
                    etiqueta={t.operador.motivoEtiqueta}
                    valor={motivo}
                    opciones={MOTIVOS}
                    onChange={(v) => setMotivo(v)}
                  />
                  <Boton variante="secundario" onClick={() => void decidir(p, "REJECTED")} deshabilitado={ocupado !== null}>
                    {t.operador.confirmarRechazo}
                  </Boton>
                </span>
              ) : null}
              <span className="comprobar-operador">
                <strong>{t.operador.comprobarTitulo}</strong>
                <ol>
                  <li>{t.operador.comprobar1(p.taxId)}</li>
                  <li>{t.operador.comprobar2}</li>
                  <li>{t.operador.comprobar3}</li>
                  <li>{t.operador.comprobar4}</li>
                </ol>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Tarjeta>
  );
}
